import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    ShieldAlert,
    Users,
    FileCheck,
    LogOut,
    FileText,
    ShieldCheck,
    Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import api from "../../api/axios";

export default function AdminLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // 🔒 SECURITY CHECK: FORCE ADMIN ROLE
    useEffect(() => {
        async function checkAdmin() {
            try {
                const res = await api.get("/auth/me");
                // Handle different response structures if necessary
                const user = res.data?.user || res.data;

                if (!user || user.role !== "ADMIN") {
                    console.warn("🚨 UNAUTHORIZED ADMIN ACCESS ATTEMPT DETECTED:", user?.id);
                    navigate("/dashboard", { replace: true });
                }
            } catch (error) {
                console.error("Admin Auth Check Failed:", error);
                navigate("/login", { replace: true });
            } finally {
                setIsLoading(false);
            }
        }
        checkAdmin();
    }, [navigate]);

    const menuItems = [
        { icon: LayoutDashboard, label: "The Pulse", path: "/admin" },
        { icon: Sparkles, label: "AYI", path: "/admin/ayi" },
        { icon: ShieldAlert, label: "Safety Queue", path: "/admin/safety" },
        { icon: FileCheck, label: "Verifications", path: "/admin/verify" },
        { icon: Users, label: "The Books", path: "/admin/users" },
        { icon: ShieldAlert, label: "Gatekeeper Params", path: "/admin/quiz" },
        { icon: FileText, label: "Printing Press", path: "/admin/articles" },
        { icon: ShieldCheck, label: "Security", path: "/admin/security" },
    ];

    if (isLoading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center text-red-600 font-mono tracking-widest animate-pulse">
                INITIALIZING RETINAL SCAN...
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-gray-300 font-sans selection:bg-red-900 selection:text-white flex overflow-hidden">
            {/* SIDEBAR */}
            <div className="w-64 border-r border-gray-800 flex flex-col relative overflow-hidden bg-black/50 backdrop-blur-xl">
                {/* Branding */}
                <div className="p-6 border-b border-gray-800 text-left">
                    <h1 className="text-xl font-bold tracking-widest text-white uppercase flex items-center gap-2">
                        <span className="text-red-600">👁️</span> The All-Seer
                    </h1>
                    <p className="text-blue-100 max-w-xl text-[10px] mt-1 tracking-widest uppercase opacity-50">
                        Central Command Interface
                    </p>
                </div>

                {/* Menu */}
                <nav className="flex-1 p-4 space-y-2">
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        const Icon = item.icon;

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 relative overflow-hidden group ${isActive
                                    ? "text-white bg-white/5 border border-white/10 shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                                    : "text-gray-500 hover:text-gray-200 hover:bg-white/5"
                                    }`}
                            >
                                <Icon className={`w-5 h-5 ${isActive ? "text-red-500" : "group-hover:text-red-400"}`} />
                                {item.label}

                                {isActive && (
                                    <motion.div
                                        layoutId="active-pill"
                                        className="absolute left-0 top-0 bottom-0 w-1 bg-red-600 shadow-[0_0_10px_#dc2626]"
                                    />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Footer: User Info */}
                <div className="p-6 border-t border-gray-800">
                    <div className="text-xs text-gray-500 font-mono">
                        SECURE CONNECTION<br />
                        {currentTime.toLocaleTimeString()}
                    </div>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="flex-1 overflow-y-auto relative bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-[#0a0a0a] to-black">
                {/* Subtle Grid Background */}
                <div className="absolute inset-0 bg-white/[0.02] opacity-20 pointer-events-none"></div>

                <div className="p-8 relative z-10 max-w-7xl mx-auto">
                    <Outlet />
                </div>
            </div>

        </div>
    );
}
